import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Target, TrendingUp, ListChecks } from 'lucide-react';

/* ── Glass card style (light theme) ── */
const glassCard = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.8)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
};

/* ── 3D Chart Illustration (SVG) ── */
const ChartIllustration = () => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
    className="flex justify-center mb-8"
  >
    <svg width="140" height="120" viewBox="0 0 140 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="70" cy="108" rx="60" ry="10" fill="url(#baseglow)" opacity="0.4" />
      <rect x="20" y="68" width="18" height="36" rx="3" fill="url(#bar1)" />
      <rect x="20" y="68" width="18" height="6" rx="3" fill="rgba(255,255,255,0.3)" />
      <rect x="46" y="48" width="18" height="56" rx="3" fill="url(#bar2)" />
      <rect x="46" y="48" width="18" height="6" rx="3" fill="rgba(255,255,255,0.3)" />
      <rect x="72" y="24" width="18" height="80" rx="3" fill="url(#bar3)" />
      <rect x="72" y="24" width="18" height="6" rx="3" fill="rgba(255,255,255,0.3)" />
      <rect x="98" y="38" width="18" height="66" rx="3" fill="url(#bar4)" />
      <rect x="98" y="38" width="18" height="6" rx="3" fill="rgba(255,255,255,0.3)" />
      <path d="M28 62 L55 42 L81 18 L107 32" stroke="#3b82f6" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <polygon points="105,25 113,32 105,35" fill="#3b82f6" />
      <circle cx="81" cy="16" r="2" fill="#60a5fa">
        <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="115" cy="26" r="1.5" fill="#93c5fd">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="68" cy="10" r="1" fill="#bfdbfe">
        <animate attributeName="opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <defs>
        <linearGradient id="bar1" x1="29" y1="68" x2="29" y2="104" gradientUnits="userSpaceOnUse">
          <stop stopColor="#93c5fd" />
          <stop offset="1" stopColor="#3b82f6" />
        </linearGradient>
        <linearGradient id="bar2" x1="55" y1="48" x2="55" y2="104" gradientUnits="userSpaceOnUse">
          <stop stopColor="#60a5fa" />
          <stop offset="1" stopColor="#2563eb" />
        </linearGradient>
        <linearGradient id="bar3" x1="81" y1="24" x2="81" y2="104" gradientUnits="userSpaceOnUse">
          <stop stopColor="#93c5fd" />
          <stop offset="1" stopColor="#3b82f6" />
        </linearGradient>
        <linearGradient id="bar4" x1="107" y1="38" x2="107" y2="104" gradientUnits="userSpaceOnUse">
          <stop stopColor="#60a5fa" />
          <stop offset="1" stopColor="#1d4ed8" />
        </linearGradient>
        <radialGradient id="baseglow" cx="0.5" cy="0.5" r="0.5">
          <stop stopColor="#93c5fd" stopOpacity="0.3" />
          <stop offset="1" stopColor="#93c5fd" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  </motion.div>
);

const WelcomeScreen = ({ onStart }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="relative flex flex-col items-center justify-center min-h-[calc(100vh-68px)] px-5">
      {/* Glass card */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
        className="relative z-10 w-full max-w-[680px] text-center rounded-2xl p-10 sm:p-14"
        style={glassCard}
        data-testid="welcome-card"
      >
        <ChartIllustration />

        {/* Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0f172a] leading-tight mb-4"
        >
          How mature is your<br />
          CRM & loyalty program?
        </motion.h1>

        {/* Subhead */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="text-sm sm:text-base text-[#64748b] leading-relaxed max-w-md mx-auto mb-10"
        >
          Answer 9 questions and get a personalised recommendations to grow revenue. Unlock:
        </motion.p>

        {/* Feature pills */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10"
        >
          {[
            { icon: Target, label: 'Maturity Score' },
            { icon: TrendingUp, label: 'Revenue Estimate' },
            { icon: ListChecks, label: 'Action Plan' },
          ].map(({ icon: Icon, label }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.55 + i * 0.08 }}
              className="flex items-center gap-2.5 px-5 py-3 rounded-xl"
              style={{
                background: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(59, 130, 246, 0.1)',
                boxShadow: '0 2px 8px rgba(31, 38, 135, 0.04)',
              }}
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#3b82f6] to-[#2563eb] flex items-center justify-center flex-shrink-0 shadow-sm">
                <Icon className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-[#334155]">{label}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.65 }}
          onClick={onStart}
          whileHover={{ scale: 1.03, boxShadow: '0 8px 30px rgba(59,130,246,0.35)' }}
          whileTap={{ scale: 0.97 }}
          className="inline-flex items-center gap-2.5 px-10 py-4 bg-gradient-to-r from-[#3b82f6] to-[#2563eb] text-white text-base font-semibold rounded-full shadow-lg cursor-pointer transition-colors"
          data-testid="start-audit-button"
        >
          Start the Audit
          <ArrowRight className="w-4 h-4" />
        </motion.button>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.75 }}
          className="mt-5 text-xs text-[#94a3b8]"
        >
          Takes about 2 minutes · No sign-up required
        </motion.p>
      </motion.div>
    </div>
  );
};

export default WelcomeScreen;
